import { Star } from 'iconoir-react'
import { cloneDeep } from 'lodash'
import { ReactElement, useState, useEffect, useRef } from 'react'
import { useDrop } from 'react-dnd'
import { UserJson } from 'app/types/entities'
import Button from '../Button'
import VisibleIcon from '../Icon/VisibleIcon'
import { DraggableSection } from './DraggableSection'
import { useSettings } from 'app/hooks/useSettings'
import type { ContactJson } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import { notifySuccess, notifyError } from 'app/services/notify.client'
import {
  getContactDrawerSections,
  SectionItem,
  NON_DRAGGABLE_SECTION_IDS,
  cloneSections,
  findSectionItem,
  filterSections,
  stripIconsForSaving,
} from 'lib/contactDrawerConfig'
import { debug, logError } from 'lib/logging'

export function ContactCustomization({
  user,
  contact,
  isIntegrated,
  onConfigSaved,
}: {
  user: UserJson
  contact?: ContactJson
  isIntegrated?: boolean
  onConfigSaved?: () => void
}): ReactElement {
  const [savedConfig, setSavedConfig] = useSettings<SectionItem[]>(`contact-drawer-config`)
  const initialSectionsData = getContactDrawerSections(savedConfig)
  // Current working state - gets modified during drag & drop operations
  const [sections, setSections] = useState<SectionItem[]>(initialSectionsData)
  // Original/initial state - used for comparison to detect changes and reset functionality
  const [initialSections, setInitialSections] = useState<SectionItem[]>(cloneDeep(initialSectionsData))
  const [isLoading, setIsLoading] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const visibleSections = filterSections(sections, isIntegrated, contact, user)

  // Auto-scroll functionality using react-dnd
  const [, dropRef] = useDrop({
    accept: `section`,
    hover: (item, monitor) => {
      const container = scrollContainerRef.current
      if (!container) return

      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return

      const containerRect = container.getBoundingClientRect()
      const scrollThreshold = 150 // Much larger zone - almost 1/3 of typical container
      const scrollSpeed = 15 // Faster base speed

      // Clear any existing scroll interval
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current)
        scrollIntervalRef.current = null
      }

      // Check if we're in the top scroll zone
      const distanceFromTop = clientOffset.y - containerRect.top
      const distanceFromBottom = containerRect.bottom - clientOffset.y

      if (distanceFromTop < scrollThreshold && container.scrollTop > 0) {
        // Progressive speed: closer to edge = faster scroll
        const intensity = 1 - distanceFromTop / scrollThreshold
        const scrollAmount = Math.max(3, Math.floor(intensity * scrollSpeed))

        scrollIntervalRef.current = setInterval(() => {
          if (container.scrollTop > 0) {
            container.scrollTop = Math.max(0, container.scrollTop - scrollAmount)
          } else {
            if (scrollIntervalRef.current) {
              clearInterval(scrollIntervalRef.current)
              scrollIntervalRef.current = null
            }
          }
        }, 16) // ~60fps
      }
      // Check if we're in the bottom scroll zone
      else if (distanceFromBottom < scrollThreshold) {
        const maxScroll = container.scrollHeight - container.clientHeight
        if (container.scrollTop < maxScroll) {
          // Progressive speed: closer to edge = faster scroll
          const intensity = 1 - distanceFromBottom / scrollThreshold
          const scrollAmount = Math.max(3, Math.floor(intensity * scrollSpeed))

          scrollIntervalRef.current = setInterval(() => {
            if (container.scrollTop < maxScroll) {
              container.scrollTop = Math.min(maxScroll, container.scrollTop + scrollAmount)
            } else {
              if (scrollIntervalRef.current) {
                clearInterval(scrollIntervalRef.current)
                scrollIntervalRef.current = null
              }
            }
          }, 16) // ~60fps
        }
      }
    },
    collect: (monitor) => {
      // Stop scrolling when drag ends
      if (!monitor.isOver() || !monitor.canDrop()) {
        if (scrollIntervalRef.current) {
          clearInterval(scrollIntervalRef.current)
          scrollIntervalRef.current = null
        }
      }
      return {}
    },
  })

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const initialSectionsData = getContactDrawerSections(savedConfig)
    setSections(initialSectionsData)
    setInitialSections(cloneDeep(initialSectionsData))
  }, [savedConfig])

  const handleDrop = (draggedId: string, targetId: string, dragParentId?: string, targetParentId?: string) => {
    setSections((prevSections) => {
      if (NON_DRAGGABLE_SECTION_IDS.includes(draggedId as any)) {
        debug([`contacts`, `customization`, `drawer`], `Blocked: draggedId is non-draggable`)
        return prevSections
      }

      if (!dragParentId && !targetParentId && NON_DRAGGABLE_SECTION_IDS.includes(targetId as any)) {
        debug([`contacts`, `customization`, `drawer`], `Blocked: targetId is non-draggable`)
        return prevSections
      }

      if (dragParentId && targetParentId && dragParentId !== targetParentId) {
        debug([`contacts`, `customization`, `drawer`], `Blocked: different parents`)
        return prevSections
      }

      if ((dragParentId && !targetParentId) || (!dragParentId && targetParentId)) {
        debug([`contacts`, `customization`, `drawer`], `Blocked: invalid level change`)
        return prevSections
      }

      const newSections = cloneSections(prevSections)

      const reorderItems = (items: SectionItem[], draggedId: string, targetId: string): boolean => {
        const dragIndex = items.findIndex((item) => item.id === draggedId)
        const dropIndex = items.findIndex((item) => item.id === targetId)

        if (dragIndex !== -1 && dropIndex !== -1 && dragIndex !== dropIndex) {
          const [removed] = items.splice(dragIndex, 1)
          const newTargetIndex = items.findIndex((item) => item.id === targetId)

          if (dragIndex > newTargetIndex) {
            items.splice(newTargetIndex, 0, removed)
          } else {
            items.splice(newTargetIndex + 1, 0, removed)
          }

          return true
        }
        return false
      }

      if (!dragParentId && !targetParentId) {
        reorderItems(newSections, draggedId, targetId)
      } else if (dragParentId && targetParentId && dragParentId === targetParentId) {
        const parent = newSections.find((item) => item.id === dragParentId)
        if (parent?.children) {
          reorderItems(parent.children, draggedId, targetId)
        } else {
          let subParent: SectionItem | null = null

          for (const section of newSections) {
            if (section.children) {
              for (const child of section.children) {
                if (child.id === dragParentId && child.children) {
                  subParent = child
                  break
                }
              }
              if (subParent) break
            }
          }

          if (subParent?.children) {
            reorderItems(subParent.children, draggedId, targetId)
          }
        }
      }

      return newSections
    })
  }

  const handleToggleVisibility = (id: string, parentId?: string) => {
    setSections((prevSections) => {
      const newSections = cloneSections(prevSections)
      const result = findSectionItem(newSections, id)

      if (result) {
        result.item.visible = !result.item.visible
      }

      return newSections
    })
  }

  const handleToggleSpotlight = (id: string, parentId?: string) => {
    setSections((prevSections) => {
      const newSections = cloneSections(prevSections)
      const spotlightSection = newSections.find((section) => section.id === `spotlight`)

      if (!spotlightSection) {
        return prevSections
      }

      if (!spotlightSection.children) {
        spotlightSection.children = []
      }

      // Check if item is already in spotlight directly
      const existingSpotlightIndex = spotlightSection.children.findIndex((child) => child.id === id)
      const isCurrentlyInSpotlight = existingSpotlightIndex !== -1

      if (isCurrentlyInSpotlight) {
        // Remove from spotlight and restore to original parent
        const itemToRestore = spotlightSection.children[existingSpotlightIndex]
        const originalParentId = itemToRestore.originalParentId

        if (!originalParentId) {
          return prevSections
        }

        // Find original parent
        let originalParent: SectionItem | undefined = newSections.find((section) => section.id === originalParentId)

        if (!originalParent) {
          for (const section of newSections) {
            if (section.children) {
              const foundChild = section.children.find((child) => child.id === originalParentId)
              if (foundChild) {
                originalParent = foundChild
                break
              }
            }
          }
        }

        if (!originalParent) {
          return prevSections
        }

        // Initialize children array if needed
        if (!originalParent.children) {
          originalParent.children = []
        }

        // Remove from spotlight first, then restore
        spotlightSection.children.splice(existingSpotlightIndex, 1)
        itemToRestore.isSpotlight = false
        const originalIndex = itemToRestore.originalIndex ?? originalParent.children.length
        delete itemToRestore.originalParentId
        delete itemToRestore.originalIndex
        // Restore to original position, clamped to valid range
        const insertIndex = Math.min(originalIndex, originalParent.children.length)
        originalParent.children.splice(insertIndex, 0, itemToRestore)
      } else {
        // Add to spotlight
        const result = findSectionItem(newSections, id)
        if (!result) return prevSections

        const { item: targetItem, parent: currentParent, index: itemIndex } = result

        if (targetItem.isCore || !currentParent || !currentParent.children) {
          return prevSections
        }

        // Remove from original parent
        currentParent.children.splice(itemIndex, 1)

        // Prepare item for spotlight
        const itemCopy: SectionItem = JSON.parse(JSON.stringify(targetItem))
        itemCopy.isSpotlight = true
        itemCopy.originalParentId = currentParent.id
        itemCopy.originalIndex = itemIndex

        // Add to spotlight
        spotlightSection.children.push(itemCopy)
      }

      return newSections
    })
  }

  const hasChanges = () => {
    const originalSavedSections = getContactDrawerSections(savedConfig, contact?.orgId)
    return JSON.stringify(sections) !== JSON.stringify(originalSavedSections)
  }

  const handleCancel = () => {
    setSections(initialSections)
    closeModal()
  }

  const handleSave = async () => {
    if (!hasChanges()) {
      closeModal()
      return
    }

    setIsLoading(true)
    try {
      // Strip icon properties before saving - they can't be serialized to JSON
      // Icons will be restored from default config when loading
      const sectionsToSave = stripIconsForSaving(sections)
      await setSavedConfig(`contact-drawer-config`, sectionsToSave)
      notifySuccess(`Contact drawer configuration saved successfully`)
      onConfigSaved?.()
      closeModal()
    } catch (error) {
      logError(`Error saving contact drawer configuration:`, error)
      notifyError(`Failed to save contact drawer configuration`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetToDefault = async () => {
    setIsLoading(true)
    try {
      const defaultSections = getContactDrawerSections(undefined, contact?.orgId)
      setSections(defaultSections)
      notifySuccess(`Contact drawer configuration reset to default`)
    } catch (error) {
      logError(`Error resetting contact drawer configuration:`, error)
      notifyError(`Failed to reset contact drawer configuration`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex h-full flex-col bg-surface-subtle">
      <div className="mb-4 flex w-full flex-col gap-1 px-6 py-4 text-body-xs text-subtle">
        <div className="flex items-center gap-1">
          Drag to rearrange items within a group. Click
          <Star width={16} height={16} className="inline-block align-middle" /> to add an item to spotlight. Click
          <VisibleIcon width={16} height={16} className="inline-block align-middle" /> to hide an item within a group.
        </div>
        <p>Note: Adding to spotlight essentially also hides the item from its original group.</p>
      </div>

      <div
        ref={(node) => {
          scrollContainerRef.current = node
          dropRef(node)
        }}
        className="flex h-[60vh] flex-col items-center overflow-y-auto px-6 pb-4"
      >
        {visibleSections.map((section, index) => (
          <DraggableSection
            key={section.id}
            section={section}
            index={index}
            onDrop={handleDrop}
            onToggleVisibility={handleToggleVisibility}
            onToggleSpotlight={handleToggleSpotlight}
            isIntegrated={isIntegrated}
            contact={contact}
          />
        ))}
      </div>

      {/* Action buttons section */}
      <div className="flex h-16 items-center justify-between border-t border-default bg-surface px-6">
        <Button mode="white" size="sm" label="Cancel" className="px-4" onClick={handleCancel} />
        <div className="flex gap-4">
          <Button
            mode="white"
            size="sm"
            label="Reset to default"
            className="px-4"
            onClick={handleResetToDefault}
            loading={isLoading}
          />
          <Button mode="primary" size="sm" label="Save" className="px-4" onClick={handleSave} loading={isLoading} />
        </div>
      </div>
    </div>
  )
}
